import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Row,
    Col,
    Spinner
} from 'reactstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/it';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarDays,
    faClock,
    faArrowRight,
    faCheck,
    faTimes,
    faBell,
    faEnvelope,
    faMobileAlt,
    faUserGroup,
    faExclamationTriangle,
    faArrowsUpDown,
    faGripLines
} from '@fortawesome/free-solid-svg-icons';

import { selectAccountData } from '../../redux/main/accountSlice';
import { getNodeApiUrl } from '../../config/api.config';

const AppointmentRescheduleModal = ({
    isOpen,
    onClose,
    appointment,
    newStart,
    newEnd,
    changeType, // 'drop' per spostamento, 'resize' per ridimensionamento
    onSuccess,
    onCancel
}) => {
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');

    // Stati per le notifiche
    const [sendCustomerEmail, setSendCustomerEmail] = useState(false);
    const [sendEmployeeEmail, setSendEmployeeEmail] = useState(false);
    const [sendEmployeePush, setSendEmployeePush] = useState(true); // Default attivo per push
    const [selectedCustomerEmails, setSelectedCustomerEmails] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [customerEmails, setCustomerEmails] = useState([]);
    const [loadingCustomer, setLoadingCustomer] = useState(false);

    // Carica le email del cliente quando si apre la modal
    useEffect(() => {
        const fetchCustomerEmails = async () => {
            if (!isOpen || !appointment?.customerId) return;
            
            // Se l'appuntamento ha già le email, usale
            if (appointment.customerEmails && appointment.customerEmails.length > 0) {
                setCustomerEmails(appointment.customerEmails);
                return;
            }
            
            setLoadingCustomer(true);
            try {
                const response = await axios.get(
                    getNodeApiUrl(`customer/${appointment.customerId}`),
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'auth-token': token
                        },
                        params: { accountId: accountData?._id }
                    }
                );
                
                if (response.data && response.data.contactsList) {
                    // Estrai le email dalla contactsList
                    const emails = response.data.contactsList
                        .filter(contact => contact.email)
                        .map(contact => ({
                            email: contact.email,
                            name: `${contact.name || ''} ${contact.surname || ''}`.trim() || contact.email,
                            isMain: contact.main || false
                        }));
                    setCustomerEmails(emails);
                }
            } catch (error) {
                console.error('Errore nel caricamento delle email del cliente:', error);
                setCustomerEmails([]);
            } finally {
                setLoadingCustomer(false);
            }
        };
        
        fetchCustomerEmails();
    }, [isOpen, appointment, token, accountData]);

    // Reset quando si apre la modal
    useEffect(() => {
        if (isOpen) {
            setSendCustomerEmail(false);
            setSendEmployeeEmail(false);
            setSendEmployeePush(true);
            setSelectedCustomerEmails([]);
        }
    }, [isOpen]);

    // Stili CSS per la modal
    useEffect(() => {
        const styleId = 'reschedule-modal-styles';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = `
                .reschedule-modal .modal-content {
                    border-radius: 12px;
                    border: none;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
                }
                .reschedule-modal .modal-header {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    border-radius: 12px 12px 0 0;
                    border: none;
                    padding: 1.25rem 1.5rem;
                }
                .reschedule-modal .modal-header .btn-close {
                    filter: brightness(0) invert(1);
                    opacity: 0.8;
                }
                .reschedule-modal .modal-header .btn-close:hover {
                    opacity: 1;
                }
                .reschedule-modal .modal-body {
                    padding: 1.5rem;
                }
                .reschedule-modal .modal-footer {
                    border-top: 1px solid #e9ecef;
                    padding: 1rem 1.5rem;
                }
                .reschedule-change-card {
                    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                    border-radius: 10px;
                    padding: 1.25rem;
                    margin-bottom: 1.5rem;
                }
                .reschedule-date-box {
                    background: white;
                    border-radius: 8px;
                    padding: 1rem;
                    text-align: center;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                }
                .reschedule-date-box.old {
                    border-left: 4px solid #dc3545;
                }
                .reschedule-date-box.new {
                    border-left: 4px solid #28a745;
                }
                .reschedule-arrow {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #667eea;
                    font-size: 1.5rem;
                }
                .notification-section {
                    background: #f8f9fa;
                    border: 1px solid #e9ecef;
                    border-radius: 10px;
                    padding: 1rem;
                    margin-bottom: 1rem;
                }
                .notification-section-title {
                    font-weight: 600;
                    margin-bottom: 0.75rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                .notification-toggle {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0.5rem 0;
                    border-bottom: 1px solid #e9ecef;
                }
                .notification-toggle:last-child {
                    border-bottom: none;
                }
                .change-type-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.35rem 0.75rem;
                    border-radius: 20px;
                    font-size: 0.85rem;
                    font-weight: 500;
                }
                .change-type-badge.drop {
                    background: #e3f2fd;
                    color: #1976d2;
                }
                .change-type-badge.resize {
                    background: #fff3e0;
                    color: #f57c00;
                }
            `;
            document.head.appendChild(style);
        }
        return () => {
            const styleElem = document.getElementById(styleId);
            if (styleElem) styleElem.remove();
        };
    }, []);

    // Gestione toggle email cliente
    const handleCustomerEmailToggle = (email, checked) => {
        if (checked) {
            setSelectedCustomerEmails(prev => [...prev, email]);
        } else {
            setSelectedCustomerEmails(prev => prev.filter(e => e !== email));
        }
    };

    // Seleziona/deseleziona tutte le email
    const handleSelectAllEmails = (checked) => {
        if (checked) {
            setSelectedCustomerEmails(customerEmails.map(c => c.email || c));
        } else {
            setSelectedCustomerEmails([]);
        }
    };

    // Conferma la modifica
    const handleConfirm = async () => {
        setIsSubmitting(true);

        try {
            const accountId = accountData?._id;
            if (!accountId) {
                toast.error('Account non trovato');
                return;
            }

            // Prepara i dati per l'aggiornamento
            const updateData = {
                _id: appointment._id,
                accountId: accountId,
                fromDate: newStart.toISOString(),
                toDate: newEnd.toISOString(),
                users: appointment.users || [],
                vehicles: appointment.vehicles || [],
                equipments: appointment.equipments || [],
                notes: appointment.notes,
                notifications: {
                    sendCustomerEmail: selectedCustomerEmails.length > 0,
                    sendEmployeeEmail: sendEmployeeEmail,
                    sendEmployeePush: sendEmployeePush,
                    customerEmails: selectedCustomerEmails
                }
            };

            // Chiamata API per aggiornare l'appuntamento
            const response = await axios.patch(
                getNodeApiUrl('appointments/updateFromPanel'),
                updateData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': token
                    }
                }
            );

            if (response.data && response.data.result) {
                const actionLabel = changeType === 'drop' ? 'spostato' : 'modificato';
                toast.success(`Appuntamento ${actionLabel} con successo!`);
                
                // Notifica di invio notifiche
                if (selectedCustomerEmails.length > 0 || sendEmployeeEmail || sendEmployeePush) {
                    const notifyMessages = [];
                    if (selectedCustomerEmails.length > 0) notifyMessages.push('cliente');
                    if (sendEmployeePush) notifyMessages.push('push collaboratori');
                    if (sendEmployeeEmail) notifyMessages.push('email collaboratori');
                    toast.info(`Notifiche inviate: ${notifyMessages.join(', ')}`);
                }
                
                if (onSuccess) {
                    onSuccess();
                }
                onClose();
            } else {
                toast.error('Errore durante l\'aggiornamento dell\'appuntamento');
                if (onCancel) onCancel();
            }
        } catch (error) {
            console.error('Errore nell\'aggiornamento dell\'appuntamento:', error);
            toast.error('Errore: ' + (error.response?.data?.message || error.message));
            if (onCancel) onCancel();
        } finally {
            setIsSubmitting(false);
        }
    };

    // Annulla la modifica
    const handleCancel = () => {
        if (onCancel) onCancel();
        onClose();
    };

    // Estrai info dall'appuntamento
    const appointmentData = appointment || {};
    const hasCustomerEmails = customerEmails.length > 0;
    const employees = appointmentData.users || [];
    const hasEmployees = employees.length > 0;

    // Calcola le date formattate
    const oldStartFormatted = appointment ? moment(appointment.fromDate).format('DD/MM/YYYY HH:mm') : '';
    const oldEndFormatted = appointment ? moment(appointment.toDate).format('DD/MM/YYYY HH:mm') : '';
    const newStartFormatted = newStart ? moment(newStart).format('DD/MM/YYYY HH:mm') : '';
    const newEndFormatted = newEnd ? moment(newEnd).format('DD/MM/YYYY HH:mm') : '';

    // Determina se le date sono cambiate
    const dateChanged = appointment && newStart && (
        moment(appointment.fromDate).format('DD/MM/YYYY') !== moment(newStart).format('DD/MM/YYYY')
    );
    const timeChanged = appointment && newStart && newEnd && (
        moment(appointment.fromDate).format('HH:mm') !== moment(newStart).format('HH:mm') ||
        moment(appointment.toDate).format('HH:mm') !== moment(newEnd).format('HH:mm')
    );

    return (
        <Modal
            isOpen={isOpen}
            toggle={handleCancel}
            centered
            size="lg"
            className="reschedule-modal"
            backdrop="static"
        >
            <ModalHeader toggle={handleCancel}>
                <FontAwesomeIcon icon={faCalendarDays} className="me-2" />
                Conferma Modifica Appuntamento
            </ModalHeader>
            <ModalBody>
                {/* Badge tipo modifica */}
                <div className="mb-3">
                    {changeType === 'drop' ? (
                        <span className="change-type-badge drop">
                            <FontAwesomeIcon icon={faCalendarDays} />
                            Spostamento appuntamento
                        </span>
                    ) : (
                        <span className="change-type-badge resize">
                            <FontAwesomeIcon icon={faArrowsUpDown} />
                            Modifica durata
                        </span>
                    )}
                </div>

                {/* Info appuntamento */}
                <div className="mb-3 p-3 bg-light rounded">
                    <h6 className="mb-2">
                        <strong>{appointmentData.title || appointmentData.jobCode || 'Appuntamento'}</strong>
                    </h6>
                    {appointmentData.customerLabel && (
                        <p className="mb-1 small text-muted">
                            <strong>Cliente:</strong> {appointmentData.customerLabel}
                        </p>
                    )}
                    {appointmentData.systemAddressLabel && (
                        <p className="mb-0 small text-muted">
                            <strong>Indirizzo:</strong> {appointmentData.systemAddressLabel}
                        </p>
                    )}
                </div>

                {/* Card cambio date/orari */}
                <div className="reschedule-change-card">
                    <Row className="align-items-center">
                        <Col md={5}>
                            <div className="reschedule-date-box old">
                                <small className="text-danger fw-bold d-block mb-2">
                                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                                    PRIMA
                                </small>
                                {dateChanged && (
                                    <div className="mb-1">
                                        <FontAwesomeIcon icon={faCalendarDays} className="me-1 text-muted" />
                                        <strong>{moment(appointment?.fromDate).format('DD/MM/YYYY')}</strong>
                                    </div>
                                )}
                                <div>
                                    <FontAwesomeIcon icon={faClock} className="me-1 text-muted" />
                                    <span>{moment(appointment?.fromDate).format('HH:mm')}</span>
                                    <span className="mx-1">-</span>
                                    <span>{moment(appointment?.toDate).format('HH:mm')}</span>
                                </div>
                            </div>
                        </Col>
                        <Col md={2} className="reschedule-arrow">
                            <FontAwesomeIcon icon={faArrowRight} />
                        </Col>
                        <Col md={5}>
                            <div className="reschedule-date-box new">
                                <small className="text-success fw-bold d-block mb-2">
                                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                                    DOPO
                                </small>
                                {dateChanged && (
                                    <div className="mb-1">
                                        <FontAwesomeIcon icon={faCalendarDays} className="me-1 text-muted" />
                                        <strong>{moment(newStart).format('DD/MM/YYYY')}</strong>
                                    </div>
                                )}
                                <div>
                                    <FontAwesomeIcon icon={faClock} className="me-1 text-muted" />
                                    <span>{moment(newStart).format('HH:mm')}</span>
                                    <span className="mx-1">-</span>
                                    <span>{moment(newEnd).format('HH:mm')}</span>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>

                {/* Sezione Notifiche */}
                <h6 className="mb-3">
                    <FontAwesomeIcon icon={faBell} className="me-2 text-warning" />
                    Notifiche
                </h6>

                {/* Notifica Cliente */}
                <div className="notification-section">
                    <div className="notification-section-title">
                        <FontAwesomeIcon icon={faEnvelope} className="text-primary" />
                        Email al Cliente
                        {hasCustomerEmails && customerEmails.length > 1 && (
                            <div className="ms-auto form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="selectAllEmails"
                                    checked={selectedCustomerEmails.length === customerEmails.length}
                                    onChange={(e) => handleSelectAllEmails(e.target.checked)}
                                />
                                <label className="form-check-label small" htmlFor="selectAllEmails">
                                    Tutti
                                </label>
                            </div>
                        )}
                    </div>

                    {loadingCustomer ? (
                        <div className="text-center py-2">
                            <Spinner size="sm" color="primary" className="me-2" />
                            <small className="text-muted">Caricamento contatti...</small>
                        </div>
                    ) : hasCustomerEmails ? (
                        <div>
                            {customerEmails.map((contact, idx) => {
                                const email = contact.email || contact;
                                const name = contact.name || email;
                                const isMain = contact.isMain || contact.main;
                                return (
                                    <div key={idx} className="form-check mb-1">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`customerEmail_${idx}`}
                                            checked={selectedCustomerEmails.includes(email)}
                                            onChange={(e) => handleCustomerEmailToggle(email, e.target.checked)}
                                        />
                                        <label className="form-check-label small" htmlFor={`customerEmail_${idx}`}>
                                            <span className="fw-medium">{name}</span>
                                            {isMain && (
                                                <span className="badge bg-primary ms-1" style={{ fontSize: '0.65rem' }}>
                                                    principale
                                                </span>
                                            )}
                                            <br />
                                            <span className="text-muted">{email}</span>
                                        </label>
                                    </div>
                                );
                            })}
                            {selectedCustomerEmails.length > 0 && (
                                <small className="text-success d-block mt-2">
                                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                                    {selectedCustomerEmails.length} email selezionat{selectedCustomerEmails.length === 1 ? 'a' : 'e'}
                                </small>
                            )}
                        </div>
                    ) : (
                        <small className="text-muted">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1 text-warning" />
                            Nessuna email associata al cliente
                        </small>
                    )}
                </div>

                {/* Notifiche Collaboratori */}
                <div className="notification-section">
                    <div className="notification-section-title">
                        <FontAwesomeIcon icon={faUserGroup} className="text-info" />
                        Notifiche ai Collaboratori
                    </div>

                    {hasEmployees ? (
                        <>
                            <div className="notification-toggle">
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faMobileAlt} className="me-2 text-success" />
                                    <span>Notifica Push</span>
                                </div>
                                <div className="form-check form-switch mb-0">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="sendEmployeePush"
                                        checked={sendEmployeePush}
                                        onChange={(e) => setSendEmployeePush(e.target.checked)}
                                    />
                                </div>
                            </div>
                            <div className="notification-toggle">
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faEnvelope} className="me-2 text-primary" />
                                    <span>Email</span>
                                </div>
                                <div className="form-check form-switch mb-0">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="sendEmployeeEmail"
                                        checked={sendEmployeeEmail}
                                        onChange={(e) => setSendEmployeeEmail(e.target.checked)}
                                    />
                                </div>
                            </div>
                            <small className="text-muted d-block mt-2">
                                <strong>Collaboratori:</strong> {employees.map(e => e.userLabel || e.label).join(', ')}
                            </small>
                        </>
                    ) : (
                        <small className="text-muted">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1 text-warning" />
                            Nessun collaboratore assegnato
                        </small>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={handleCancel} disabled={isSubmitting}>
                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                    Annulla
                </Button>
                <Button color="success" onClick={handleConfirm} disabled={isSubmitting}>
                    {isSubmitting ? (
                        <>
                            <Spinner size="sm" className="me-1" />
                            Salvataggio...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faCheck} className="me-1" />
                            Conferma Modifica
                        </>
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default AppointmentRescheduleModal;
